import { AutocompleteWithLabel } from '@adotel/components'
import { useQuery } from '@apollo/client'
import { FormikErrors, FormikTouched } from 'formik'
import { useEffect, useState } from 'react'

import SectionTitle from '../../components/SectionTitle'
import { CURRENCIES_QUERY } from '../../graphql/currencies'
import { timezones } from '../../helpers/timezones'
import {
  getTranslate,
  languagesList,
  normalizeLanguageCode
} from '../../localization'
import { Currency } from '../../types/currency'
import { EntityList } from '../../types/entityList'
import { UserForCreateAndModify } from '../../types/users'
import useStyles from './styles/userSetting.styles'

interface UserSettingProps {
  values: UserForCreateAndModify
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  errors: FormikErrors<UserForCreateAndModify>
  touched: FormikTouched<UserForCreateAndModify>
  handleBlur: {
    (e: React.FocusEvent<any, Element>): void
    <T = any>(fieldOrEvent: T): T extends string ? (e: any) => void : void
  }
  isSubmitting: boolean
  params: {
    id: string
  }
}

const UserSetting: React.FC<UserSettingProps> = ({
  values,
  setFieldValue,
  errors,
  touched,
  handleBlur,
  isSubmitting
}) => {
  const classes = useStyles()
  const [autoCompleteForLanguages, setAutoCompleteForLanguages] = useState<
    any[]
  >([])
  const [autoCompleteForCurrencies, setAutoCompleteForCurrencies] = useState<
    any[]
  >([])
  const [autoCompleteForDefaultCurrency, setAutoCompleteForDefaultCurrency] =
    useState<any>(null)
  const [autoCompleteLabelForTimezone, setAutoCompleteLabelForTimezone] =
    useState<any>(null)

  const { data: currenciesData, loading: loadingForCurrencies } = useQuery<{
    currencies: Currency[]
  }>(CURRENCIES_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const languagesOptions: EntityList[] = languagesList.map((lang) => {
    return {
      label: lang.key,
      value: lang.key,
      entityCode: '',
      displayEntityCode: '',
      id: ''
    }
  })

  const currenciesOptions = currenciesData
    ? currenciesData.currencies.map((curr) => ({
        value: curr as string,
        label: curr as string
      }))
    : []

  useEffect(() => {
    const languages = values?.regionalSettings?.languagesAvailable ?? []

    /*
     * Normalised to match `languagesOptions` above, which is built from
     * `languagesList` and is therefore uppercase. A stored value may be lowercase
     * — 11 of 16 stage users hold `["en"]` or `["fa","en"]` — and an unnormalised
     * chip matches no option, so the field showed a selection the option list did
     * not agree with. Editing anything else on the form could then save a
     * different set of languages than the admin believed was selected.
     *
     * Saving afterwards writes the canonical form back, so opening and saving a
     * user quietly repairs the record.
     */
    setAutoCompleteForLanguages(
      languages.filter(Boolean).map((lang) => ({
        label: normalizeLanguageCode(lang),
        value: normalizeLanguageCode(lang)
      }))
    )
  }, [values?.regionalSettings?.languagesAvailable])

  useEffect(() => {
    const currencies = values.regionalSettings.currenciesAvailable ?? []
    setAutoCompleteForCurrencies(
      currencies.map((currency) => ({
        label: currency,
        value: currency
      }))
    )
  }, [values.regionalSettings.currenciesAvailable])

  useEffect(() => {
    setAutoCompleteForDefaultCurrency({
      label: values.regionalSettings.defaultCurrency,
      value: values.regionalSettings.defaultCurrency
    })
  }, [values.regionalSettings.defaultCurrency])

  useEffect(() => {
    const timezone = timezones.find((tz) =>
      tz.utc.includes(values.regionalSettings.timezone)
    )

    setAutoCompleteLabelForTimezone(
      timezone
        ? {
            value: values.regionalSettings.timezone,
            label: timezone.text || ''
          }
        : null
    )
  }, [values.regionalSettings.timezone])

  return (
    <div>
      <SectionTitle title={getTranslate('Setting')} marginBottom />
      <div className={classes.inputsContainer}>
        <AutocompleteWithLabel
          name="regionalSettings.languagesAvailable"
          options={languagesOptions}
          size="small"
          multiple
          showSelectAll
          limitTags={3}
          getOptionLabel={(option) => option.label || ''}
          label={getTranslate('Languages')}
          placeholder={getTranslate('Select languages')}
          value={autoCompleteForLanguages}
          inputSize="small"
          onChange={(_, v: any) => {
            const selectedLanguages: string[] = []
            v.forEach((value: any) => {
              selectedLanguages.push(value.value)
              setFieldValue(
                'regionalSettings.languagesAvailable',
                selectedLanguages
              )
            })

            if (v.length > 0) {
              setAutoCompleteForLanguages(
                v.map((value: any) => ({
                  value: value.value,
                  label: value.label
                }))
              )
            } else {
              setAutoCompleteForLanguages([])
              setFieldValue('regionalSettings.languagesAvailable', [])
            }
          }}
          error={
            (touched.regionalSettings?.languagesAvailable &&
              !!errors.regionalSettings?.languagesAvailable) ||
            undefined
          }
          helpertext={
            touched.regionalSettings?.languagesAvailable &&
            !!errors.regionalSettings?.languagesAvailable
              ? getTranslate(
                  (errors.regionalSettings?.languagesAvailable as string) || ''
                )
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          loadingText={getTranslate('Loading') + '...'}
        />
        <AutocompleteWithLabel
          name="regionalSettings.currenciesAvailable"
          options={currenciesOptions}
          size="small"
          multiple
          showSelectAll
          limitTags={3}
          getOptionLabel={(option) => option.label || ''}
          label={getTranslate('Currencies')}
          placeholder={getTranslate('Select Currencies')}
          value={autoCompleteForCurrencies}
          inputSize="small"
          onChange={(_, v: any) => {
            const selectedCurrencies: Currency[] = []
            v.forEach((value: any) => {
              selectedCurrencies.push(value.value)
              setFieldValue(
                'regionalSettings.currenciesAvailable',
                selectedCurrencies
              )
            })

            if (v.length > 0) {
              setAutoCompleteForCurrencies(
                v.map((value: any) => ({
                  value: value.value,
                  label: value.label
                }))
              )
            } else {
              setAutoCompleteForCurrencies([])
              setFieldValue('regionalSettings.currenciesAvailable', [])
            }
          }}
          error={
            (touched.regionalSettings?.currenciesAvailable &&
              !!errors.regionalSettings?.currenciesAvailable) ||
            undefined
          }
          helpertext={
            touched.regionalSettings?.currenciesAvailable &&
            !!errors.regionalSettings?.currenciesAvailable
              ? getTranslate(
                  (errors.regionalSettings?.currenciesAvailable as string) || ''
                )
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          loadingText={getTranslate('Loading') + '...'}
        />
        <AutocompleteWithLabel
          name="regionalSettings.defaultCurrency"
          options={currenciesOptions.filter((option) => {
            const list = autoCompleteForCurrencies.map((ele) => ele.value)
            return list.includes(option.value)
          })}
          size="small"
          getOptionLabel={(option) => option.label || ''}
          label={getTranslate('Default Currency')}
          placeholder={getTranslate('Select default currency')}
          value={autoCompleteForDefaultCurrency}
          inputSize="small"
          onChange={(_, v: any) => {
            if (v) {
              setFieldValue('regionalSettings.defaultCurrency', v.value)
              setAutoCompleteForDefaultCurrency({
                value: v.value,
                label: v.label
              })
            } else {
              setFieldValue('regionalSettings.defaultCurrency', undefined)
              setAutoCompleteForDefaultCurrency(null)
            }
          }}
          error={
            (touched.regionalSettings?.defaultCurrency &&
              !!errors.regionalSettings?.defaultCurrency) ||
            undefined
          }
          helpertext={
            touched.regionalSettings?.defaultCurrency
              ? getTranslate(errors.regionalSettings?.defaultCurrency || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting || autoCompleteForCurrencies.length < 1}
          loading={loadingForCurrencies}
          loadingText={getTranslate('Loading') + '...'}
        />
        <AutocompleteWithLabel
          name="regionalSettings.timezone"
          label={getTranslate('Timezone')}
          placeholder={getTranslate('Select timezone')}
          onChange={(event, v: any) => {
            if (v) {
              setFieldValue('regionalSettings.timezone', v.value)
              setAutoCompleteLabelForTimezone({
                value: v.value,
                label: v.label
              })
            } else {
              setFieldValue('regionalSettings.timezone', '')
              setAutoCompleteLabelForTimezone(null)
            }
          }}
          getOptionLabel={(option) => option?.label || ''}
          options={timezones.map((item) => ({
            label: item.text,
            value: item.utc[0]
          }))}
          renderOption={(option) => <>{option.label}</>}
          getOptionSelected={(option, value) => option.value === value.value}
          size="small"
          value={autoCompleteLabelForTimezone}
          includeInputInList
          error={
            (touched.regionalSettings?.timezone &&
              !!errors.regionalSettings?.timezone) ||
            undefined
          }
          helpertext={
            touched.regionalSettings?.timezone
              ? getTranslate(errors.regionalSettings?.timezone || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
          loadingText={getTranslate('Loading') + '...'}
        />
      </div>
    </div>
  )
}
export default UserSetting
